import { Button } from "@/Button";
import { Divider } from "@/Divider";
import { LayoutHeader } from "@/Header";
import { ValidateInput } from "@/Input";
import { SequentialAnimation } from "@/SequentialAnimation";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { IconMungSmall } from "@/Icon/MungSmall";
import { useUserInfo, useUpdateUserInfo } from "~/services/customer/myPage";
import { Dialog } from "@/Dialog";
import { formatPhoneNumber } from "%/dataTransform";

export const Route = createFileRoute("/customer/layout/user-info-Chg")({
  component: RouteComponent,
});

interface UserInfoForm {
  email: string;
  userName: string;
  nickname: string;
  phoneNumber: string;
}

function RouteComponent() {
  const { data: userInfo, isLoading } = useUserInfo();
  const updateMutation = useUpdateUserInfo();

  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "알림",
    message1: "",
    message2: "",
    message3: "",
  });

  const showModal = (
    message1: string,
    message2: string = "",
    message3: string = "",
    title: string = "알림"
  ) => {
    setModalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<UserInfoForm>({
    defaultValues: {
      email: userInfo?.email,
      userName: userInfo?.userName,
      nickname: userInfo?.nickname,
      phoneNumber: userInfo?.phoneNumber,
    },
    mode: "onChange",
  });

  // API 데이터를 폼에 설정
  useEffect(() => {
    if (userInfo) {
      setValue("email", userInfo.email);
      setValue("userName", userInfo.userName);
      setValue("nickname", userInfo.nickname);
      setValue("phoneNumber", formatPhoneNumber(userInfo.phoneNumber));
    }
  }, [userInfo, setValue]);

  const bindSubmit = async (data: UserInfoForm) => {
    try {
      const updateData = {
        userName: data.userName,
        nickname: data.nickname,
        phoneNumber: data.phoneNumber.replace(/-/g, ""),
      };

      await updateMutation.mutateAsync(updateData);
      showModal("정보가 성공적으로 수정되었습니다.", "", "", "저장 완료");
    } catch (error) {
      console.error("정보 수정 중 오류가 발생했습니다:", error);
      showModal(
        "정보 수정 중 오류가 발생했습니다.",
        "다시 시도해주세요.",
        "",
        "오류"
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <LayoutHeader title={"계정정보 변경"} hasBackBtn hasFootPrint={false} />
        <div className="flex flex-auto justify-center items-center">
          <div>로딩 중...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title={"계정정보 변경"} hasBackBtn hasFootPrint={false} />
      {/* 수신자 */}
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        <form className="flex flex-col flex-auto gap-4 px-5 pt-5 rounded-t-3xl">
          <SequentialAnimation>
            <div className="text-[16px] font-semibold">계정 설정</div>
            <ValidateInput
              className="w-full"
              placeholder="이메일 주소를 입력하세요"
              label="이메일 주소"
              disabled
              {...register("email")}
            />
            <Divider className="mt-auto" />
            <ValidateInput
              className="w-full"
              label="이름"
              placeholder="이름을 입력하세요"
              errorMessage={errors?.userName?.message}
              {...register("userName", {
                required: { value: true, message: "이름을 입력하세요" },
                minLength: {
                  value: 2,
                  message: "이름은 최소 2글자 이상이어야 합니다",
                },
                pattern: {
                  value: /^[가-힣]+$/,
                  message: "한글만 입력 가능합니다",
                },
              })}
            />
            <ValidateInput
              className="w-full"
              label="휴대폰번호"
              type="tel"
              placeholder="휴대폰 번호를 입력하세요"
              errorMessage={errors?.phoneNumber?.message}
              {...register("phoneNumber", {
                required: {
                  value: true,
                  message: "휴대폰 번호를 입력하세요",
                },
                pattern: {
                  value: /^\d{3}-\d{3,4}-\d{4}$/,
                  message: "유효한 휴대폰 번호를 입력하세요",
                },
              })}
            />
            <ValidateInput
              className="w-full"
              label="집사님 닉네임"
              placeholder="집사님 닉네임을 입력하세요."
              errorMessage={errors?.nickname?.message}
              {...register("nickname", {
                required: { value: true, message: "닉네임을 입력하세요" },
                minLength: {
                  value: 2,
                  message: "닉네임은 최소 2글자 이상이어야 합니다",
                },
              })}
            />
          </SequentialAnimation>
          <div className="flex gap-2 justify-center px-5 my-5">
            <IconMungSmall />
            <div className="text-[13px] text-[#ED8432]">
              변경된 정보는 유치원 등록정보에도 반영됩니다.
            </div>
          </div>
        </form>
      </div>
      {/* 버튼을 Nav와 같은 레벨에서 렌더링 */}
      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-[500px] z-40 px-5 bg-[#F5F5F5]"
        style={{ bottom: "calc(var(--nav-bottom, 0px) + 88px)" }}
      >
        <Button
          className="w-full bg-[#F5F5F5]"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit(bindSubmit)}
          disabled={!isValid || updateMutation.isPending}
        >
          <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
            {updateMutation.isPending ? "저장 중..." : "정보 수정"}
          </div>
        </Button>
      </div>
      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {modalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {modalMessage.message1}
            </div>
            {modalMessage.message2 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message2}
              </div>
            )}
            {modalMessage.message3 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message3}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
